import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Col, Flex, message, Row, Select, Space, Table, Typography } from 'antd';
import { EditOutlined, EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import { usePageTitle } from '../../../context/pageTitleContext';
import BulkImportServices from '../../../services/bulkImportServices';
import dayjs from 'dayjs';
import ImportLogsDetails from './importLogsDetails';
import { orderUploadStatusComp } from '../../../utils/functions';
import { useUserAuth } from '../../../context/UserContext';
import { logTypesCode, logTypesNames, orderUploadStatus, tablePageSize } from '../../../utils/constants';
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useSelector } from 'react-redux';
import { Logs } from '../../../types/commonTypes';
import { roles } from '../../../utils/permissionUtils';

dayjs.extend(utc);
dayjs.extend(timezone);
const { Text } = Typography;
const autoRefreshSeconds = 10;

const initialFilters = {
    name: null,
    surname: null,
    email: null,
    roleID: null,
    enabled: true
};

const ImportLogs = () => {
    const { t } = useTranslation();
    const { user } = useUserAuth();
    const { setPageTitle } = usePageTitle();
    const [filters, setFilters] = useState(initialFilters);
    const [logsList, seLogsList] = useState<Logs[]>([]);
    const [loadingLogList, setLoadingLogList] = useState(false);
    const [logDetailsVisibility, setLogDetailsVisibility] = useState(false);
    const [selectedLog, setSelectedLog] = useState<string | null>(null);
    const [enableAutoRefresh, setEnableAutoRefresh] = useState(false);
    const [countdown, setCountdown] = useState(autoRefreshSeconds);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);       // For API refresh
    const countdownRef = useRef<NodeJS.Timeout | null>(null);     // For countdown text
    const { logTypes } = useSelector((state: any) => state.utils);
    const [pageNumber, setPageNumber] = useState(1);
    const [sorterColumn, setSorterColumn] = useState("");
    const [totalCountRecords, setTotalCountRecords] = useState<number | null>(null);
    const [selectedLogTypeId, setSelectedLogTypeId] = useState<string | null>(null);

    useEffect(() => {
        setPageTitle(t('page.importLogs'));
    }, []);

    useEffect(() => {               
        if (user.roleCode === roles.d2c && logTypes.length > 0) {
            let d2cLogType = logTypes.find((logType: any) => logType.code === logTypesCode.orderD2C);
            setSelectedLogTypeId(d2cLogType?.id);           
        }
        else{
            let defaultLogType = logTypes.find((logType: any) => logType.code === logTypesCode.order);
            setSelectedLogTypeId(defaultLogType?.id);     
        }

    }, [logTypes]);

    useEffect(() => {
        if (selectedLogTypeId) {
            getLogs();
        }
    }, [pageNumber, selectedLogTypeId]);

    useEffect(() => {
        return () => {
            // Cleanup interval on unmount
            if (intervalRef.current) clearInterval(intervalRef.current);
            if (countdownRef.current) clearInterval(countdownRef.current);
        };
    }, []);


    const getLogs = async () => {
        const payload = {
            userID: user.id,
            pageNumber: pageNumber,
            pageSize: tablePageSize,
            logTypeID: selectedLogTypeId
        }
        setLoadingLogList(true);
        try {
            const respData = await BulkImportServices.getLogs(payload);
            if (respData.success) {
                const res = respData.responseObject.value.executionLogs;
                seLogsList(res);

                const hasActiveLogs = res.some((log: any) =>
                    log.status !== orderUploadStatus.failed
                    && log.status !== orderUploadStatus.success
                    && log.status !== orderUploadStatus.partiallyProcessed
                    && log.status !== orderUploadStatus.completed
                    && log.status === null
                    && log.status === ''
                );

                if (hasActiveLogs) {
                    // Only set interval if it doesn't already exist
                    if (!intervalRef.current) {
                        intervalRef.current = setInterval(() => {
                            getLogs();       // refresh data
                            setCountdown(autoRefreshSeconds); // reset countdown
                        }, 10000);
                    }

                    // Start countdown timer (updates every 1s)
                    if (!countdownRef.current) {
                        countdownRef.current = setInterval(() => {
                            setCountdown(prev => {
                                if (prev <= 1) return autoRefreshSeconds;
                                return prev - 1;
                            });
                        }, 1000);
                    }


                } else {
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }
                    if (countdownRef.current) {
                        clearInterval(countdownRef.current);
                        countdownRef.current = null;
                    }
                    setCountdown(autoRefreshSeconds); // reset when stopped
                }

                if (res && res.length > 0)
                    setTotalCountRecords(res[0].totalCount || 0);

            }
            else {
                message.error(t('messages.errorAccured'));
            }
        } catch (error) {
            message.error(t('messages.errorAccured'));
        } finally {
            setLoadingLogList(false);
        }
    };

    const onLogDetailsSelect = (record: any) => {
        setLogDetailsVisibility(true);
        setSelectedLog(record);
        
    }

    const onLogDetailsClose = () => {
        setSelectedLog(null);
        setLogDetailsVisibility(false);
    }

    const onPageOrOrderChange = (pagination: any, filters: any, sorter: any) => {
        setPageNumber(pagination.current);
        if (sorter && sorter.field) {
            let sorterQuote = "";
            if (sorter.order === "descend") {
                sorterQuote = "[" + sorter.field + "]" + " " + "DESC";
            }
            if (sorter.order === "ascend") {
                sorterQuote = "[" + sorter.field + "]" + " " + "ASC";
            }
            setSorterColumn(sorterQuote);
        } else {
            setSorterColumn("");
        }
    };

    const onRefresh = () => {
        getLogs();
        setPageNumber(1);
    }

    const columns = [
        {
            title: '',
            key: 'action',
            width: "30px",
            fixed: 'left',
            render: (text: any, record: any) => {
                return (
                    <>                       
                            <Space size="middle">
                                <EyeOutlined onClick={() => onLogDetailsSelect(record)} className='editIcon' />
                            </Space>                      
                    </>
                )
            },
        },
        {
            title: t('logs.fileName'),
            dataIndex: 'fileName',
            key: 'fileName'
        },
        {
            title: t('logs.username'),
            dataIndex: 'userName',
            key: 'userName'
        },
        {
            title: t('logs.logTypeName'),
            dataIndex: 'logTypeName',
            key: 'logTypeName'
        },
        {
            title: t('logs.rowsProcessed'),
            dataIndex: 'rowsProcessed',
            key: 'rowsProcessed'
        },
        {
            title: t('logs.rowsFailed'),
            dataIndex: 'rowsFailed',
            key: 'rowsFailed'
        },
        {
            title: t('logs.validationsFailed'),
            dataIndex: 'validationsFailed',
            key: 'validationsFailed'
        },
        {
            title: t('logs.startTime'),
            dataIndex: 'startTime',
            key: 'startTime',
            // render: (text) => (text ? dayjs(text).format('DD/MM/YYYY - HH:mm') : "")
            render: (text: any) => {
                return (text ? dayjs.utc(text).tz(dayjs.tz.guess()).format('DD/MM/YYYY - HH:mm') : "")
            }
        },
        {
            title: t('logs.endTime'),
            dataIndex: 'endTime',
            key: 'endTime',
            render: (text: any) => {
                return (text ? dayjs.utc(text).tz(dayjs.tz.guess()).format('DD/MM/YYYY - HH:mm') : "")
            }
        },
        {
            title: t('logs.status'),
            dataIndex: 'status',
            key: 'status',
            render: (text: any, record: any) => {
                return (orderUploadStatusComp(record.status, t))
            }
        }
    ];

    return (
        <div className="logList">

            {selectedLog && (
                <ImportLogsDetails
                    visibility={logDetailsVisibility}
                    onClose={onLogDetailsClose}
                    selectedLog={selectedLog}
                />
            )}

            <Card>

                <Row gutter={{ lg: 24 }} className='mb10'>
                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} style={{ padding: 0 }}>
                        <Flex align="center" gap="small"
                            style={{ padding: '10px', marginBottom: '5px', backgroundColor: '#f5f5f5', justifyContent: 'space-between' }}>
                            <Select
                                style={{ width: '150px' }}
                                value={selectedLogTypeId}
                                placeholder={t('logs.selectLogType')}
                                options={logTypes.map((type: any) => ({ label: type.description, value: type.id }))}
                                onChange={(value) => setSelectedLogTypeId(value)}
                            />

                            <Space direction='horizontal' align='center'>
                                <Button onClick={() => onRefresh()} type='dashed' icon={<ReloadOutlined />}>{t('general.refreshData')}</Button>
                                {intervalRef.current && (
                                    <Text style={{ fontSize: 14, color: "#8f8f8f" }}>
                                        Auto-refreshing in {countdown}{countdown !== 1 ? '' : ''}
                                    </Text>
                                )}
                            </Space>
                        </Flex>
                    </Col>
                </Row>


                <Row gutter={{ lg: 24 }} className='mb10'>
                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} style={{ padding: 0 }}>
                        <Table
                            size='small'
                            columns={columns as any}
                            dataSource={logsList}
                            rowKey={(obj: any) => obj.logID}
                            loading={loadingLogList}
                            bordered
                            onChange={(pagination, filters, sorter) =>
                                onPageOrOrderChange(pagination, filters, sorter)
                            }
                            pagination={{
                                pageSize: tablePageSize,
                                showSizeChanger: false,
                                current: pageNumber,
                                total: totalCountRecords || 0
                            }}
                        />
                    </Col>
                </Row>
            </Card>
        </div>
    );
}
export default ImportLogs;